import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Stack,
  Paper,
  Chip,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
import AppleIcon from '@mui/icons-material/Apple';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import { useStyle } from '../../context/styleContext/styleContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Seo from '../../components/Seo';
import { Link as RouterLink } from 'react-router-dom';

export default function MyGear() {
  const { style } = useStyle();
  const [gear, setGear] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('nameAsc');
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener('resize', handleResize);
    getMyGear();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getMyGear = async () => {
    const token = localStorage.getItem('token');

    try {
      const { data } = await axios.post('/api/get_my_gear', { token });

      if (data.error) {
        toast.error(data.error);
      } else {
        setGear(data.products || []);
        setLicenses(data.licenses || []);
      }
    } catch (error) {
      console.log(error);
      toast.error('Unable to load your gear right now.');
    }

    setIsLoading(false);
  };

  const getLicenseBySKU = (sku) =>
    licenses.find((l) => l.sku === sku || l.SKU === sku);

  const handleOpenDevicesModal = (license) => {
    setSelectedLicense(license);
    setIsModalOpen(true);
  };

  const handleCloseDevicesModal = () => {
    setSelectedLicense(null);
    setIsModalOpen(false);
  };

  const handleRemoveDevice = async (deviceId) => {
    const token = localStorage.getItem('token');

    try {
      const { data } = await axios.post('/api/remove_device_from_license', {
        token,
        licenseID: selectedLicense._id,
        ipAddress: deviceId,
      });

      if (data.success) {
        setSelectedLicense(data.updatedLicense);
        toast.success('Device removed');
        getMyGear();
      } else {
        toast.error(data.error || 'Failed to remove device');
      }
    } catch (err) {
      toast.error('Error removing device');
      console.error(err);
    }
  };

  const filteredGear = useMemo(() => {
    return [...gear]
      .filter((item) => {
        if (filter === 'Mac' && !item.macDownloadURI) return false;
        if (filter === 'Windows' && !item.windowsDownloadURI) return false;
        return item.name.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => {
        if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
        if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
        return 0;
      });
  }, [gear, filter, searchTerm, sortOption]);

  const getLicenseTypeChip = (license) => {
    if (!license?.type) return null;

    if (license.type === 'own') {
      return (
        <Chip
          icon={<WorkspacePremiumRoundedIcon />}
          label="Owned"
          size="small"
          sx={{
            color: style.color4,
            backgroundColor: 'rgba(0,180,60,0.18)',
            border: '1px solid rgba(0,200,80,0.35)',
            '& .MuiChip-icon': { color: style.color4 },
          }}
        />
      );
    }

    if (license.type === 'subscribe') {
      return (
        <Chip
          icon={<AutorenewRoundedIcon />}
          label="Subscription"
          size="small"
          sx={{
            color: style.color4,
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.10)',
            '& .MuiChip-icon': { color: style.color4 },
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      <Seo
        title="My Gear | Parish Audio"
        description="View your Parish Audio plugins, downloads, licenses, and installation details."
        canonical="https://parishaudio.com/my-gear"
        robots="noindex,follow"
      />

      <Box
        sx={{
          width: '95%',
          maxWidth: 1000,
          mx: 'auto',
          py: 4,
          minHeight: height - 140,
          overflowX: 'hidden',
        }}
      >
        {isLoading ? (
          <Box sx={{ textAlign: 'center', mt: 10 }}>
            <LoadingSpinner />
          </Box>
        ) : gear.length === 0 ? (
          <Paper
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 3,
              backgroundColor: style.color1,
              color: style.color4,
              boxShadow: 10,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.4rem' },
                fontWeight: 800,
                mb: 1,
                color: style.color4,
              }}
            >
              Your gear library is empty
            </Typography>

            <Typography
              sx={{
                color: style.color3,
                maxWidth: 620,
                mx: 'auto',
                lineHeight: 1.8,
                mb: 3,
              }}
            >
              When you purchase or subscribe to a plugin, it will appear here along with your
              download links and device management options.
            </Typography>

            <Button
              component={RouterLink}
              to="/plugins"
              variant="contained"
              sx={{
                backgroundColor: style.color2,
                color: style.color4,
                fontWeight: 700,
                px: 3,
                py: 1.2,
                '&:hover': {
                  backgroundColor: style.color2,
                  filter: 'brightness(1.08)',
                },
              }}
            >
              Shop All Plugins
            </Button>
          </Paper>
        ) : (
          <Stack spacing={3}>
            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 3,
                backgroundColor: style.color1,
                color: style.color4,
                boxShadow: 10,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      fontWeight: 800,
                      mb: 0.75,
                      color: style.color4,
                    }}
                  >
                    My Gear
                  </Typography>
                </Box>

                <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
                    <InputLabel sx={{ color: style.color4 }}>Platform</InputLabel>
                    <Select
                      value={filter}
                      label="Platform"
                      onChange={(e) => setFilter(e.target.value)}
                      sx={{
                        color: style.color4,
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        '.MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255,255,255,0.12)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: style.color2,
                        },
                        '& .MuiSvgIcon-root': {
                          color: style.color4,
                        },
                      }}
                    >
                      <MenuItem value="All">All Platforms</MenuItem>
                      <MenuItem value="Mac">Mac</MenuItem>
                      <MenuItem value="Windows">Windows</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
                    <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
                    <Select
                      value={sortOption}
                      label="Sort"
                      onChange={(e) => setSortOption(e.target.value)}
                      sx={{
                        color: style.color4,
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        '.MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255,255,255,0.12)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: style.color2,
                        },
                        '& .MuiSvgIcon-root': {
                          color: style.color4,
                        },
                      }}
                    >
                      <MenuItem value="nameAsc">Name A–Z</MenuItem>
                      <MenuItem value="nameDesc">Name Z–A</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search your plugins..."
                    sx={{
                      flex: 1,
                      minWidth: { xs: '100%', md: 260 },
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      '& .MuiOutlinedInput-root': {
                        color: style.color4,
                        '& fieldset': {
                          borderColor: 'rgba(255,255,255,0.12)',
                        },
                        '&:hover fieldset': {
                          borderColor: style.color2,
                        },
                      },
                      '& input::placeholder': {
                        color: style.color3,
                        opacity: 1,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: style.color3 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Box sx={{ width: '100%', overflowX: 'hidden' }}>
              <Grid
                container
                spacing={{ xs: 1.5, sm: 2, md: 2.25 }}
                sx={{ width: '100%', mx: 0 }}
              >
                {filteredGear.map((product) => {
                  const license = getLicenseBySKU(product.SKU);
                  const machines = license?.machinesInstalledOn || [];

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={product.SKU}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          backgroundColor: style.color1,
                          color: style.color4,
                          borderRadius: 3,
                          boxShadow: 8,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            minHeight: 190,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: style.darkerPanel,
                          }}
                        >
                          <Box
                            component="a"
                            href={`/products/${product.productURI}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '100%',
                              height: '100%',
                              textDecoration: 'none',
                              //backgroundColor: style.darkPanel,
                              //p:1,
                            }}
                          >
                            <Box
                              component="img"
                              src={product.images?.[0]?.URI || '/default-image.png'}
                              alt={product.images?.[0]?.altText || 'Product image'}
                              sx={{
                                maxHeight: 165,
                                maxWidth: '100%',
                                objectFit: 'contain',
                                display: 'block',
                              }}
                            />
                          </Box>
                        </Box>

                        <CardContent
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            p: 1.75,
                            '&:last-child': { pb: 1.75 },
                          }}
                        >
                          <Stack spacing={1.25} sx={{ flex: 1 }}>
                            <Box>
                              <Typography
                                component="a"
                                href={`/products/${product.productURI}`}
                                sx={{
                                  color: style.color4,
                                  fontSize: '1.05rem',
                                  fontWeight: 800,
                                  textDecoration: 'none',
                                  lineHeight: 1.35,
                                  '&:hover': { color: style.color3 },
                                }}
                              >
                                {product.name}
                              </Typography>
                            </Box>

                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {getLicenseTypeChip(license)}
                              <Chip
                                icon={<DevicesRoundedIcon />}
                                label={`${machines.length} device${machines.length !== 1 ? 's' : ''}`}
                                size="small"
                                sx={{
                                  color: style.color4,
                                  backgroundColor: 'rgba(255,255,255,0.05)',
                                  border: '1px solid rgba(255,255,255,0.08)',
                                  '& .MuiChip-icon': { color: style.color4 },
                                }}
                              />
                            </Stack>
                          </Stack>

                          <Divider sx={{ my: 1.5, backgroundColor: 'rgba(255,255,255,0.08)' }} />

                          <Stack spacing={0.5}>
                            <Typography sx={{ color: style.color4, fontWeight: 700, fontSize: '0.95rem' }}>
                              Download:
                            </Typography>

                            <Stack direction="row" spacing={0.5}>
                              {product.windowsDownloadURI && (
                                <Button
                                  href={product.windowsDownloadURI}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outlined"
                                  startIcon={<DesktopWindowsRoundedIcon />}
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    borderColor: style.color2,
                                    color: style.color4,
                                    fontWeight: 700,
                                    py: 0.9,
                                    fontSize: '0.84rem',
                                    '&:hover': {
                                      borderColor: style.color4,
                                      backgroundColor: 'rgba(255,255,255,0.04)',
                                    },
                                  }}
                                >
                                  Windows
                                </Button>
                              )}

                              {product.macDownloadURI && (
                                <Button
                                  href={product.macDownloadURI}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outlined"
                                  startIcon={<AppleIcon />}
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    borderColor: style.color2,
                                    color: style.color4,
                                    fontWeight: 700,
                                    py: 0.9,
                                    fontSize: '0.84rem',
                                    '&:hover': {
                                      borderColor: style.color4,
                                      backgroundColor: 'rgba(255,255,255,0.04)',
                                    },
                                  }}
                                >
                                  Mac
                                </Button>
                              )}
                            </Stack>

                            <Button
                              variant="text"
                              startIcon={<DevicesRoundedIcon />}
                              onClick={() => handleOpenDevicesModal(license)}
                              disabled={!license || machines.length === 0}
                              sx={{
                                alignSelf: 'flex-start',
                                color: style.color3,
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                px: 0,
                                '&.Mui-disabled': {
                                  color: 'rgba(255,255,255,0.28)',
                                },
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: style.color4,
                                },
                              }}
                            >
                              {machines.length > 0 ? 'Manage Devices' : 'No Devices Yet'}
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Stack>
        )}

        <Dialog
          open={isModalOpen}
          onClose={handleCloseDevicesModal}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              backgroundColor: style.color1,
              color: style.color4,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: style.color2,
              color: style.color4,
              py: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
                {selectedLicense?.productName || 'Manage Devices'}
              </Typography>
              <Typography sx={{ fontSize: '0.92rem', opacity: 0.9 }}>
                Devices currently authorized for this plugin
              </Typography>
            </Box>

            <IconButton onClick={handleCloseDevicesModal} sx={{ color: style.color4 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ backgroundColor: style.color1, color: style.color4, p: 2.5 }}>
            <Stack spacing={2}>
              {!selectedLicense?.machinesInstalledOn?.length ? (
                <Typography sx={{ color: style.color3 }}>
                  No active devices were found for this license yet.
                </Typography>
              ) : (
                selectedLicense.machinesInstalledOn.map((device) => (
                  <Paper
                    key={device.ipAddress}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(255,255,255,0.035)',
                      boxShadow: 3,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <Typography sx={{ color: style.color3, fontSize: '0.86rem', mb: 0.25 }}>
                          IP Address
                        </Typography>
                        <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                          {device.ipAddress}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={5}>
                        <Typography sx={{ color: style.color3, fontSize: '0.86rem', mb: 0.25 }}>
                          Device
                        </Typography>
                        <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                          {device.deviceName || 'Unknown Device'}
                        </Typography>
                        <Typography sx={{ color: style.color3, fontSize: '0.9rem' }}>
                          {device.osName || 'Unknown OS'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                          <Button
                            variant="outlined"
                            onClick={() => handleRemoveDevice(device.ipAddress)}
                            sx={{
                              color: style.color4,
                              borderColor: style.color2,
                              fontWeight: 700,
                              minWidth: 130,
                              '&:hover': {
                                borderColor: style.color4,
                                backgroundColor: 'rgba(255,255,255,0.04)',
                              },
                            }}
                          >
                            Remove Device
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ backgroundColor: style.color1, px: 2.5, pb: 2.5 }}>
            <Button
              onClick={handleCloseDevicesModal}
              variant="contained"
              sx={{
                backgroundColor: style.color2,
                color: style.color4,
                fontWeight: 700,
                px: 3,
                '&:hover': {
                  backgroundColor: style.color2,
                  filter: 'brightness(1.08)',
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

// import React, { useState, useEffect, useMemo } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Divider,
//   IconButton,
//   Stack,
//   Paper,
//   Chip,
//   InputAdornment,
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
// import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
// import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
// import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
// import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
// import AppleIcon from '@mui/icons-material/Apple';
// import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';
// import Seo from '../../components/Seo';
// import { Link as RouterLink } from 'react-router-dom';

// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');
//   const [selectedLicense, setSelectedLicense] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [height, setHeight] = useState(window.innerHeight);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }

//     window.addEventListener('resize', handleResize);
//     getMyGear();

//     return () => {
//       window.removeEventListener('resize', handleResize);
//     };
//   }, []);

//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');

//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });

//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products || []);
//         setLicenses(data.licenses || []);
//       }
//     } catch (error) {
//       console.log(error);
//       toast.error('Unable to load your gear right now.');
//     }

//     setIsLoading(false);
//   };

//   const getLicenseBySKU = (sku) =>
//     licenses.find((l) => l.sku === sku || l.SKU === sku);

//   const handleOpenDevicesModal = (license) => {
//     setSelectedLicense(license);
//     setIsModalOpen(true);
//   };

//   const handleCloseDevicesModal = () => {
//     setSelectedLicense(null);
//     setIsModalOpen(false);
//   };

//   const handleRemoveDevice = async (deviceId) => {
//     const token = localStorage.getItem('token');

//     try {
//       const { data } = await axios.post('/api/remove_device_from_license', {
//         token,
//         licenseID: selectedLicense._id,
//         ipAddress: deviceId,
//       });

//       if (data.success) {
//         setSelectedLicense(data.updatedLicense);
//         toast.success('Device removed');
//         getMyGear();
//       } else {
//         toast.error(data.error || 'Failed to remove device');
//       }
//     } catch (err) {
//       toast.error('Error removing device');
//       console.error(err);
//     }
//   };

//   const filteredGear = useMemo(() => {
//     return [...gear]
//       .filter((item) => {
//         if (filter === 'Mac' && !item.macDownloadURI) return false;
//         if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//         return item.name.toLowerCase().includes(searchTerm.toLowerCase());
//       })
//       .sort((a, b) => {
//         if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//         if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//         return 0;
//       });
//   }, [gear, filter, searchTerm, sortOption]);

//   const totalDevices = licenses.reduce((sum, license) => {
//     const machines = license?.machinesInstalledOn || [];
//     return sum + machines.length;
//   }, 0);

//   return (
//     <>
//       <Seo
//         title="My Gear | Parish Audio"
//         description="View your Parish Audio plugins, downloads, licenses, and installation details."
//         canonical="https://parishaudio.com/my-gear"
//         robots="noindex,follow"
//       />

//       <Box sx={{ width: '95%', maxWidth: 1200, mx: 'auto', py: 4, minHeight: height - 140 }}>
//         {isLoading ? (
//           <Box sx={{ textAlign: 'center', mt: 10 }}>
//             <LoadingSpinner />
//           </Box>
//         ) : gear.length === 0 ? (
//           <Paper
//             sx={{
//               p: { xs: 3, md: 5 },
//               borderRadius: 3,
//               backgroundColor: style.color1,
//               color: style.color4,
//               boxShadow: 10,
//               textAlign: 'center',
//             }}
//           >
//             <ExtensionRoundedIcon sx={{ fontSize: 56, color: style.color2, mb: 1 }} />
//             <Typography
//               variant="h1"
//               sx={{
//                 fontSize: { xs: '2rem', md: '2.4rem' },
//                 fontWeight: 800,
//                 mb: 1,
//                 color: style.color4,
//               }}
//             >
//               Your gear library is empty
//             </Typography>
//             <Typography
//               sx={{
//                 color: style.color3,
//                 maxWidth: 620,
//                 mx: 'auto',
//                 lineHeight: 1.8,
//                 mb: 3,
//               }}
//             >
//               When you purchase or claim a plugin, it will appear here along with your
//               download links and device management options.
//             </Typography>

//             <Button
//               component={RouterLink}
//               to="/plugins"
//               variant="contained"
//               sx={{
//                 backgroundColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 3,
//                 py: 1.2,
//                 '&:hover': {
//                   backgroundColor: style.color2,
//                   filter: 'brightness(1.08)',
//                 },
//               }}
//             >
//               Shop All Plugins
//             </Button>
//           </Paper>
//         ) : (
//           <Stack spacing={3}>
//             <Paper
//               sx={{
//                 p: { xs: 2.5, md: 3 },
//                 borderRadius: 3,
//                 backgroundColor: style.color1,
//                 color: style.color4,
//                 boxShadow: 10,
//               }}
//             >
//               <Stack spacing={2.5}>
//                 <Box>
//                   <Typography
//                     variant="h1"
//                     sx={{
//                       fontSize: { xs: '2rem', md: '2.8rem' },
//                       fontWeight: 800,
//                       mb: 1,
//                       color: style.color4,
//                     }}
//                   >
//                     My Gear
//                   </Typography>

//                   <Typography
//                     sx={{
//                       color: style.color3,
//                       lineHeight: 1.8,
//                       maxWidth: 800,
//                     }}
//                   >
//                     Download your plugins, view your active devices, and manage your licenses
//                     from one place.
//                   </Typography>
//                 </Box>

//                 <Grid container spacing={2}>
//                   <Grid item xs={12} md={4}>
//                     <Paper
//                       sx={{
//                         p: 2,
//                         borderRadius: 2.5,
//                         backgroundColor: 'rgba(255,255,255,0.035)',
//                         boxShadow: 4,
//                         height: '100%',
//                       }}
//                     >
//                       <Typography sx={{ color: style.color3, fontSize: '0.95rem' }}>
//                         Plugins in your library
//                       </Typography>
//                       <Typography sx={{ color: style.color4, fontSize: '2rem', fontWeight: 900 }}>
//                         {gear.length}
//                       </Typography>
//                     </Paper>
//                   </Grid>

//                   <Grid item xs={12} md={4}>
//                     <Paper
//                       sx={{
//                         p: 2,
//                         borderRadius: 2.5,
//                         backgroundColor: 'rgba(255,255,255,0.035)',
//                         boxShadow: 4,
//                         height: '100%',
//                       }}
//                     >
//                       <Typography sx={{ color: style.color3, fontSize: '0.95rem' }}>
//                         Active licenses
//                       </Typography>
//                       <Typography sx={{ color: style.color4, fontSize: '2rem', fontWeight: 900 }}>
//                         {licenses.length}
//                       </Typography>
//                     </Paper>
//                   </Grid>

//                   <Grid item xs={12} md={4}>
//                     <Paper
//                       sx={{
//                         p: 2,
//                         borderRadius: 2.5,
//                         backgroundColor: 'rgba(255,255,255,0.035)',
//                         boxShadow: 4,
//                         height: '100%',
//                       }}
//                     >
//                       <Typography sx={{ color: style.color3, fontSize: '0.95rem' }}>
//                         Total connected devices
//                       </Typography>
//                       <Typography sx={{ color: style.color4, fontSize: '2rem', fontWeight: 900 }}>
//                         {totalDevices}
//                       </Typography>
//                     </Paper>
//                   </Grid>
//                 </Grid>

//                 <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

//                 <Stack
//                   direction={{ xs: 'column', md: 'row' }}
//                   spacing={2}
//                   alignItems={{ xs: 'stretch', md: 'center' }}
//                 >
//                   <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
//                     <InputLabel sx={{ color: style.color4 }}>Platform</InputLabel>
//                     <Select
//                       value={filter}
//                       label="Platform"
//                       onChange={(e) => setFilter(e.target.value)}
//                       sx={{
//                         color: style.color4,
//                         backgroundColor: 'rgba(255,255,255,0.03)',
//                         '.MuiOutlinedInput-notchedOutline': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover .MuiOutlinedInput-notchedOutline': {
//                           borderColor: style.color2,
//                         },
//                         '& .MuiSvgIcon-root': {
//                           color: style.color4,
//                         },
//                       }}
//                     >
//                       <MenuItem value="All">All Platforms</MenuItem>
//                       <MenuItem value="Mac">Mac</MenuItem>
//                       <MenuItem value="Windows">Windows</MenuItem>
//                     </Select>
//                   </FormControl>

//                   <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
//                     <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                     <Select
//                       value={sortOption}
//                       label="Sort"
//                       onChange={(e) => setSortOption(e.target.value)}
//                       sx={{
//                         color: style.color4,
//                         backgroundColor: 'rgba(255,255,255,0.03)',
//                         '.MuiOutlinedInput-notchedOutline': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover .MuiOutlinedInput-notchedOutline': {
//                           borderColor: style.color2,
//                         },
//                         '& .MuiSvgIcon-root': {
//                           color: style.color4,
//                         },
//                       }}
//                     >
//                       <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                       <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                     </Select>
//                   </FormControl>

//                   <TextField
//                     size="small"
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                     placeholder="Search your plugins..."
//                     sx={{
//                       flex: 1,
//                       minWidth: { xs: '100%', md: 260 },
//                       backgroundColor: 'rgba(255,255,255,0.03)',
//                       '& .MuiOutlinedInput-root': {
//                         color: style.color4,
//                         '& fieldset': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover fieldset': {
//                           borderColor: style.color2,
//                         },
//                       },
//                       '& input::placeholder': {
//                         color: style.color3,
//                         opacity: 1,
//                       },
//                     }}
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <SearchRoundedIcon sx={{ color: style.color3 }} />
//                         </InputAdornment>
//                       ),
//                     }}
//                   />
//                 </Stack>
//               </Stack>
//             </Paper>

//             <Grid container spacing={3}>
//               {filteredGear.map((product) => {
//                 const license = getLicenseBySKU(product.SKU);
//                 const machines = license?.machinesInstalledOn || [];

//                 return (
//                   <Grid item xs={12} sm={6} lg={4} key={product.SKU}>
//                     <Card
//                       sx={{
//                         height: '100%',
//                         display: 'flex',
//                         flexDirection: 'column',
//                         backgroundColor: style.color1,
//                         color: style.color4,
//                         borderRadius: 3,
//                         boxShadow: 10,
//                         overflow: 'hidden',
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           p: 2,
//                           minHeight: 220,
//                           display: 'flex',
//                           alignItems: 'center',
//                           justifyContent: 'center',
//                           backgroundColor: 'rgba(255,255,255,0.025)',
//                         }}
//                       >
//                         <Box
//                           component="a"
//                           href={`/products/${product.productURI}`}
//                           sx={{
//                             display: 'flex',
//                             alignItems: 'center',
//                             justifyContent: 'center',
//                             width: '100%',
//                             height: '100%',
//                             textDecoration: 'none',
//                           }}
//                         >
//                           <Box
//                             component="img"
//                             src={product.images?.[0]?.URI || '/default-image.png'}
//                             alt={product.images?.[0]?.altText || 'Product image'}
//                             sx={{
//                               maxHeight: 180,
//                               maxWidth: '100%',
//                               objectFit: 'contain',
//                               display: 'block',
//                             }}
//                           />
//                         </Box>
//                       </Box>

//                       <CardContent
//                         sx={{
//                           display: 'flex',
//                           flexDirection: 'column',
//                           flex: 1,
//                           p: 2.25,
//                           '&:last-child': { pb: 2.25 },
//                         }}
//                       >
//                         <Stack spacing={1.5} sx={{ flex: 1 }}>
//                           <Box>
//                             <Typography
//                               component="a"
//                               href={`/products/${product.productURI}`}
//                               sx={{
//                                 color: style.color4,
//                                 fontSize: '1.2rem',
//                                 fontWeight: 800,
//                                 textDecoration: 'none',
//                                 '&:hover': { color: style.color3 },
//                               }}
//                             >
//                               {product.name}
//                             </Typography>

//                             <Typography
//                               sx={{
//                                 mt: 0.8,
//                                 color: style.color3,
//                                 lineHeight: 1.7,
//                                 fontSize: '0.94rem',
//                               }}
//                             >
//                               {product.shortDescription || product.cardSummary || 'Audio plugin'}
//                             </Typography>
//                           </Box>

//                           <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
//                             <Chip
//                               icon={<CheckCircleRoundedIcon />}
//                               label="Licensed"
//                               size="small"
//                               sx={{
//                                 color: style.color4,
//                                 backgroundColor: 'rgba(0,180,60,0.18)',
//                                 border: '1px solid rgba(0,200,80,0.35)',
//                                 '& .MuiChip-icon': { color: style.color4 },
//                               }}
//                             />

//                             <Chip
//                               icon={<DevicesRoundedIcon />}
//                               label={`${machines.length} device${machines.length !== 1 ? 's' : ''}`}
//                               size="small"
//                               sx={{
//                                 color: style.color4,
//                                 backgroundColor: 'rgba(255,255,255,0.05)',
//                                 border: '1px solid rgba(255,255,255,0.08)',
//                                 '& .MuiChip-icon': { color: style.color4 },
//                               }}
//                             />
//                           </Stack>

//                           <Paper
//                             sx={{
//                               p: 1.5,
//                               borderRadius: 2,
//                               backgroundColor: 'rgba(255,255,255,0.035)',
//                               boxShadow: 2,
//                             }}
//                           >
//                             <Stack direction="row" spacing={1} alignItems="flex-start">
//                               <InfoOutlinedIcon sx={{ color: style.color2, mt: '2px', fontSize: '1.1rem' }} />
//                               <Typography sx={{ color: style.color3, fontSize: '0.92rem', lineHeight: 1.65 }}>
//                                 Download and install the plugin, then open it in your DAW and
//                                 sign in with your Parish Audio account to unlock it.
//                               </Typography>
//                             </Stack>
//                           </Paper>
//                         </Stack>

//                         <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.08)' }} />

//                         <Stack spacing={1.25}>
//                           <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                             Downloads
//                           </Typography>

//                           <Stack direction="row" spacing={1.2}>
//                             {product.windowsDownloadURI && (
//                               <Button
//                                 href={product.windowsDownloadURI}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 variant="outlined"
//                                 startIcon={<DesktopWindowsRoundedIcon />}
//                                 sx={{
//                                   flex: 1,
//                                   borderColor: style.color2,
//                                   color: style.color4,
//                                   fontWeight: 700,
//                                   py: 1.1,
//                                   '&:hover': {
//                                     borderColor: style.color4,
//                                     backgroundColor: 'rgba(255,255,255,0.04)',
//                                   },
//                                 }}
//                               >
//                                 Windows
//                               </Button>
//                             )}

//                             {product.macDownloadURI && (
//                               <Button
//                                 href={product.macDownloadURI}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 variant="outlined"
//                                 startIcon={<AppleIcon />}
//                                 sx={{
//                                   flex: 1,
//                                   borderColor: style.color2,
//                                   color: style.color4,
//                                   fontWeight: 700,
//                                   py: 1.1,
//                                   '&:hover': {
//                                     borderColor: style.color4,
//                                     backgroundColor: 'rgba(255,255,255,0.04)',
//                                   },
//                                 }}
//                               >
//                                 Mac
//                               </Button>
//                             )}
//                           </Stack>

//                           <Button
//                             variant="text"
//                             startIcon={<DevicesRoundedIcon />}
//                             onClick={() => handleOpenDevicesModal(license)}
//                             disabled={!license || machines.length === 0}
//                             sx={{
//                               alignSelf: 'flex-start',
//                               color: style.color3,
//                               fontWeight: 700,
//                               px: 0,
//                               '&.Mui-disabled': {
//                                 color: 'rgba(255,255,255,0.28)',
//                               },
//                               '&:hover': {
//                                 backgroundColor: 'transparent',
//                                 color: style.color4,
//                               },
//                             }}
//                           >
//                             {machines.length > 0 ? 'Manage Devices' : 'No Devices Yet'}
//                           </Button>
//                         </Stack>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 );
//               })}
//             </Grid>
//           </Stack>
//         )}

//         <Dialog
//           open={isModalOpen}
//           onClose={handleCloseDevicesModal}
//           fullWidth
//           maxWidth="md"
//           PaperProps={{
//             sx: {
//               borderRadius: 3,
//               overflow: 'hidden',
//               backgroundColor: style.color1,
//               color: style.color4,
//             },
//           }}
//         >
//           <DialogTitle
//             sx={{
//               display: 'flex',
//               justifyContent: 'space-between',
//               alignItems: 'center',
//               backgroundColor: style.color2,
//               color: style.color4,
//               py: 1.5,
//             }}
//           >
//             <Box>
//               <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
//                 {selectedLicense?.productName || 'Manage Devices'}
//               </Typography>
//               <Typography sx={{ fontSize: '0.92rem', opacity: 0.9 }}>
//                 Devices currently authorized for this plugin
//               </Typography>
//             </Box>

//             <IconButton onClick={handleCloseDevicesModal} sx={{ color: style.color4 }}>
//               <CloseIcon />
//             </IconButton>
//           </DialogTitle>

//           <DialogContent dividers sx={{ backgroundColor: style.color1, color: style.color4, p: 2.5 }}>
//             <Stack spacing={2}>
//               {!selectedLicense?.machinesInstalledOn?.length ? (
//                 <Typography sx={{ color: style.color3 }}>
//                   No active devices were found for this license yet.
//                 </Typography>
//               ) : (
//                 selectedLicense.machinesInstalledOn.map((device) => (
//                   <Paper
//                     key={device.ipAddress}
//                     sx={{
//                       p: 2,
//                       borderRadius: 2.5,
//                       backgroundColor: 'rgba(255,255,255,0.035)',
//                       boxShadow: 3,
//                     }}
//                   >
//                     <Grid container spacing={2} alignItems="center">
//                       <Grid item xs={12} md={3}>
//                         <Typography sx={{ color: style.color3, fontSize: '0.86rem', mb: 0.25 }}>
//                           IP Address
//                         </Typography>
//                         <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                           {device.ipAddress}
//                         </Typography>
//                       </Grid>

//                       <Grid item xs={12} md={5}>
//                         <Typography sx={{ color: style.color3, fontSize: '0.86rem', mb: 0.25 }}>
//                           Device
//                         </Typography>
//                         <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                           {device.deviceName || 'Unknown Device'}
//                         </Typography>
//                         <Typography sx={{ color: style.color3, fontSize: '0.9rem' }}>
//                           {device.osName || 'Unknown OS'}
//                         </Typography>
//                       </Grid>

//                       <Grid item xs={12} md={4}>
//                         <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
//                           <Button
//                             variant="outlined"
//                             onClick={() => handleRemoveDevice(device.ipAddress)}
//                             sx={{
//                               color: style.color4,
//                               borderColor: style.color2,
//                               fontWeight: 700,
//                               minWidth: 130,
//                               '&:hover': {
//                                 borderColor: style.color4,
//                                 backgroundColor: 'rgba(255,255,255,0.04)',
//                               },
//                             }}
//                           >
//                             Remove Device
//                           </Button>
//                         </Box>
//                       </Grid>
//                     </Grid>
//                   </Paper>
//                 ))
//               )}
//             </Stack>
//           </DialogContent>

//           <DialogActions sx={{ backgroundColor: style.color1, px: 2.5, pb: 2.5 }}>
//             <Button
//               onClick={handleCloseDevicesModal}
//               variant="contained"
//               sx={{
//                 backgroundColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 3,
//                 '&:hover': {
//                   backgroundColor: style.color2,
//                   filter: 'brightness(1.08)',
//                 },
//               }}
//             >
//               Close
//             </Button>
//           </DialogActions>
//         </Dialog>
//       </Box>
//     </>
//   );
// }

// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   CardMedia,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Divider,
//   IconButton
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';
// import Seo from '../../components/Seo';


// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');
//   const [selectedLicense, setSelectedLicense] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);

//   const [width, setWidth] = useState(window.innerWidth


//   )
// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     getMyGear();
    
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;


//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');
//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });
//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products);
//         setLicenses(data.licenses);
//       }
//     } catch (error) {
//       console.log(error);
//     }
//     setIsLoading(false);
//   };

//   const getLicenseBySKU = (sku) => licenses.find((l) => l.sku === sku);

//   const handleOpenDevicesModal = (license) => {
//     setSelectedLicense(license);
//     setIsModalOpen(true);
//   };

//   const handleCloseDevicesModal = () => {
//     setSelectedLicense(null);
//     setIsModalOpen(false);
//   };

//   const handleRemoveDevice = async (deviceId) => {
//     const token = localStorage.getItem('token');
// console.log("IP?????")
// console.log(selectedLicense.ipAddress);
//     try {
//       console.log(selectedLicense)

//       const { data } = await axios.post('/api/remove_device_from_license', {
//         token,
//         licenseID: selectedLicense._id,
//         ipAddress: deviceId,
//       });
//       if (data.success) {
//         console.log("Updated license")
//         console.log(data.updatedLicense);
//         console.log("XXXXX")
//         setSelectedLicense(data.updatedLicense)
//         toast.success('Device removed');
//         getMyGear();
//         //handleCloseDevicesModal();
//       } else {
//         toast.error(data.error || 'Failed to remove device');
//       }
//     } catch (err) {
//       toast.error('Error removing device');
//       console.error(err);
//     }
//   };

//   const filteredGear = gear
//     .filter((item) => {
//       if (filter === 'Mac' && !item.macDownloadURI) return false;
//       if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//       return item.name.toLowerCase().includes(searchTerm.toLowerCase());
//     })
//     .sort((a, b) => {
//       if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//       if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//       return 0;
//     });

//   return (
//     <>
//     <Seo
//   title="My Gear | Parish Audio"
//   description="View your Parish Audio plugins, downloads, licenses, and installation details."
//   canonical="https://parishaudio.com/my-gear"
//   robots="noindex,follow"
// />
    
//     <div className='image-background-1'>
//       <div className='dark-alpha-80'>
//         <Box sx={{ minHeight: '100vh', color: style.color4 }}>
//           {isLoading ? (
//             <Box sx={{ textAlign: 'center', mt: 10 }}><LoadingSpinner /></Box>
//           ) : (
//             <>
//               {gear.length === 0 ? (
//                 <Box textAlign="center" mt={10}>
//                   <Typography variant="h5">You do not yet have any equipment.</Typography>
//                   <Box mt={3}>
//                     <a href="/plugins" style={{ color: style.color3 }}>
//                       <Typography variant="h6">Shop All Plugins</Typography>
//                     </a>
//                   </Box>
//                 </Box>
//               ) : (
//                 <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
//                   <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
//                     <FormControl sx={{ minWidth: 180 }}>
//                       <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                       <Select
//                         value={sortOption}
//                         onChange={(e) => setSortOption(e.target.value)}
//                         size="small"
//                         sx={{ color: style.color4, backgroundColor: style.color1 }}
//                       >
//                         <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                         <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                       </Select>
//                     </FormControl>

//                     <TextField
//                       variant="outlined"
//                       placeholder="Search by name..."
//                       value={searchTerm}
//                       size="small"
//                       onChange={(e) => setSearchTerm(e.target.value)}
//                       sx={{ minWidth: 250, backgroundColor: style.color1, input: { color: style.color4 } }}
//                     />
//                   </Box>

//                   <Grid container spacing={3}>
//                     {filteredGear.map((product) => {
//                       const license = getLicenseBySKU(product.SKU);
//                       const machines = license?.machinesInstalledOn || [];

//                       return (
//                         <Grid item xs={12} sm={6} md={4} key={product.SKU}>
//                           <Card sx={{ backgroundColor: style.color1, color: style.color4 }}>
//                             <a href={`/products/${product.productURI}`}>
//                               <CardMedia
//                                 component="img"
//                                 image={product.images?.[0]?.URI || '/default-image.png'}
//                                 alt={product.images?.[0]?.altText || 'Product image'}
//                                 sx={{ maxHeight: 200, maxWidth:200, objectFit: 'contain', p: 0, margin:'auto',  mt:2 }}
//                               />
//                             </a>
//                             <CardContent>
//                               <Typography variant="h6">
//                                 <a href={`/products/${product.productURI}`} style={{ color: style.color4 }}><strong>{product.name}</strong></a>
//                               </Typography>
//                               <Divider className='divider1' />
//                               <div style={{display:'flex', flexDirection:'row' }}>
//                                 <Typography variant="body1" sx={{ mt: 1 }}>

//                                 Installed on {machines.length} device{machines.length !== 1 ? 's' : ''}
//                               </Typography>
//                               {machines.length > 0 && (
//                                 <Button variant="outlined" size="small" onClick={() => handleOpenDevicesModal(license)} sx={{ mt: 1, ml: 1, borderColor:style.color2, backgroundColor: style.color1, color:style.color4, '&:hover': {backgroundColor: style.color2, color:'white' } }}>
//                                   Manage Devices
//                                 </Button>
//                               )}

//                               </div>
//                               <Typography variant="h6">
//                                 <strong>Download</strong>
//                                 </Typography>
//                               <div style={{display:'flex', flexDirection:'row'}}>
//                               {product.windowsDownloadURI && (
//                                 <Button href={product.windowsDownloadURI} target="_blank" variant="contained" sx={{ mt: 1, pt:2, margin:'auto', backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' } }}>
//                                   <div style={{display:'flex', flexDirection:'column'}}>
//                                     <div style={{backgroundColor:'black', padding:'5px', paddingLeft:'10px', paddingRight:'10px', paddingTop:'10px'}}>
//                                       <img src='windows_logo.png' style={{maxWidth:'80px', maxHeight:'80px', margin:'auto'}} />
//                                     </div>
//                                     <div style={{textAlign:'center'}}>
//                                       <strong>Windows</strong>
//                                     </div>
//                                   </div>
              
//                                 </Button>
//                               )}
//                               {product.macDownloadURI && (
//                                 <Button href={product.macDownloadURI} target="_blank" variant="contained" sx={{ mt: 1, pt:2, margin:'auto', backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' } }}>
//                                 <div style={{display:'flex', flexDirection:'column'}}>
//                                   <div style={{backgroundColor:style.color4, padding:'5px', paddingLeft:'10px', paddingRight:'10px', paddingTop:'10px'}}>
//                                     <img src='apple_logo.svg' style={{maxWidth:'80px', maxHeight:'80px', margin:'auto'}} />
//                                   </div>
//                                   <div style={{textAlign:'center'}}>
//                                     <strong>Mac</strong>
//                                   </div>
//                                 </div>
            
//                               </Button>
//                               )}

//                               </div>
//                             </CardContent>
//                           </Card>
//                         </Grid>
//                       );
//                     })}
//                   </Grid>
//                 </Box>
//               )}
//             </>
//           )}

//           <Dialog open={isModalOpen} onClose={handleCloseDevicesModal} fullWidth maxWidth="md">
//             {!isMobile &&(
//               <>
//               <DialogTitle size="small" sx={{ display: 'flex', pt:0, pb:0,justifyContent: 'space-between', alignItems: 'center', backgroundColor: style.color2, color: style.color4 }}>
//                <span style={{fontSize:'1.2em', marginTop:'0px', marginBottom:'0px'}}>{selectedLicense?.productName}</span>
//               <IconButton onClick={handleCloseDevicesModal} sx={{ color: style.color4 }}>
//                 <CloseIcon />
//               </IconButton>
//             </DialogTitle>
            
//             <DialogContent dividers sx={{ backgroundColor: style.color1, color: style.color4 }}>
//               <div>

//               <h2 style={{fontSize:'1em', marginTop:'0px', marginBottom:'15px'}}>
//               Current Devices:
//               </h2>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt:0 }}>
//               <Typography sx={{fontSize:"1.2em", mr:1, ml:.5}}><strong>IP Address</strong></Typography>
//               <Typography sx={{fontSize:"1.2em", mr:1, ml:.5}}><strong>Device</strong></Typography>
//               <Typography sx={{fontSize:"1.2em", mr:1, ml:.5}}><strong>Remove </strong></Typography>
//                 </Box>
//               {selectedLicense?.machinesInstalledOn.map((device) => (
                
//                 <Box key={device.ipAddress} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt:0, p:1 }}>
//                   <Typography sx={{fontSize:"1em", mr:.5, ml:.5}}>{device.ipAddress}</Typography>
//                   <Typography sx={{fontSize:"1em", mr:.5, ml:.5}}>{device.deviceName} ({device.osName})</Typography>
//                   <Button variant="outlined" sx={{ mt: 1, color:style.color4, borderColor:style.color3, minWidth:'75px', borderWidth:'1px', borderStyle:'solid',  backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' }}} size="small" onClick={() => handleRemoveDevice(device.ipAddress)}>
//                     Remove
//                   </Button>
//                 </Box>
//               ))}
//               </div>
//             </DialogContent>
//             <DialogActions sx={{ backgroundColor: style.color1 }}>

//               <Button sx={{ mt: 1, borderColor:style.color3, minWidth:'150px', borderWidth:'1px', borderStyle:'solid',  backgroundColor:style.color2, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' }}} onClick={handleCloseDevicesModal} variant="contained">
//                 <strong>Close</strong>
//               </Button>
//             </DialogActions>

//               </>
//             )}




//               {isMobile &&(
//               <>
//               <DialogTitle size="small" sx={{ display: 'flex', pt:0, pb:0,justifyContent: 'space-between', alignItems: 'center', backgroundColor: style.color2, color: style.color4 }}>
//                <span style={{fontSize:'1.2em', marginTop:'0px', marginBottom:'0px'}}>{selectedLicense?.productName}</span>
//               <IconButton onClick={handleCloseDevicesModal} sx={{ color: style.color4 }}>
//                 <CloseIcon />
//               </IconButton>
//             </DialogTitle>
            
//             <DialogContent dividers sx={{ backgroundColor: style.color1, color: style.color4 }}>
//             <h2 style={{fontSize:'1em', marginTop:'0px', marginBottom:'15px'}}>
//               Current Devices:
//               </h2>
//               <Box sx={{display:'flex', flexDirection:'column'}}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt:0 }}>
//               {/* <Typography sx={{fontSize:"1em", mr:1, ml:.5}}><strong>IP Address</strong></Typography>
//               <Typography sx={{fontSize:"1em", mr:1, ml:.5}}><strong>Device</strong></Typography>
//               <Typography sx={{fontSize:"1em", mr:1, ml:.5}}><strong>Remove </strong></Typography> */}
//                 </Box>
//               {selectedLicense?.machinesInstalledOn.map((device) => (
                
//                 <Box key={device.ipAddress} sx={{ display: 'flex', flexDirection:'column', justifyContent: 'space-between', mb: 2, mt:0, p:1, boxShadow:5, borderColor:style.color2, borderWidth:'1px', borderStyle:'solid' }}>
//                   <Typography sx={{fontSize:"1em", mr:.5, ml:.5}}>IP Address: {device.ipAddress}</Typography>
//                   <Typography sx={{fontSize:"1em", mr:.5, ml:.5}}>Device: {device.deviceName}</Typography>
//                   <Button variant="outlined" sx={{ mt: 1, color:style.color4, borderColor:style.color3, minWidth:'75px', borderWidth:'1px', borderStyle:'solid',  backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' }}} size="small" onClick={() => handleRemoveDevice(device.ipAddress)}>
//                     Remove
//                   </Button>
//                 </Box>
//               ))}
//               </Box>
//             </DialogContent>
//             <DialogActions sx={{ backgroundColor: style.color1 }}>

//               <Button sx={{ mt: 1, borderColor:style.color3, minWidth:'150px', borderWidth:'1px', borderStyle:'solid',  backgroundColor:style.color2, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' }}} onClick={handleCloseDevicesModal} variant="contained">
//                 <strong>Close</strong>
//               </Button>
//             </DialogActions>

//               </>
//             )}

            
//           </Dialog>
//         </Box>
//       </div>
//     </div>
//     </>
//   );
// }



// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   CardMedia,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField
// } from '@mui/material';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';

// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');
//   const [selectedLicense, setSelectedLicense] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);

//   useEffect(() => {
//     document.title = 'Parish Audio | My Gear';
//     getMyGear();
//   }, []);

//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');
//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });
//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products);
//         setLicenses(data.licenses);
//       }
//     } catch (error) {
//       console.log(error);
//     }
//     setIsLoading(false);
//   };

//   const getLicenseBySKU = (sku) => licenses.find((l) => l.sku === sku);

//   const handleOpenDevicesModal = (license) => {
//     setSelectedLicense(license);
//     setIsModalOpen(true);
//   };

//   const handleCloseDevicesModal = () => {
//     setSelectedLicense(null);
//     setIsModalOpen(false);
//   };

//   const handleRemoveDevice = async (deviceId) => {
//     const token = localStorage.getItem('token');
//     try {
//       const { data } = await axios.post('/api/remove_device_from_license', {
//         token,
//         licenseId: selectedLicense._id,
//         deviceId,
//       });
//       if (data.success) {
//         toast.success('Device removed');
//         getMyGear();
//         handleCloseDevicesModal();
//       } else {
//         toast.error(data.error || 'Failed to remove device');
//       }
//     } catch (err) {
//       toast.error('Error removing device');
//       console.error(err);
//     }
//   };

//   const filteredGear = gear
//     .filter((item) => {
//       if (filter === 'Mac' && !item.macDownloadURI) return false;
//       if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//       return item.name.toLowerCase().includes(searchTerm.toLowerCase());
//     })
//     .sort((a, b) => {
//       if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//       if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//       return 0;
//     });

//   return (
//     <div className='image-background-1'>
//       <div className='dark-alpha-80'>
//         <Box sx={{ minHeight: '100vh', color: style.color4 }}>
//           {isLoading ? (
//             <Box sx={{ textAlign: 'center', mt: 10 }}><LoadingSpinner /></Box>
//           ) : (
//             <>
//               {gear.length === 0 ? (
//                 <Box textAlign="center" mt={10}>
//                   <Typography variant="h5">You do not yet have any equipment.</Typography>
//                   <Box mt={3}>
//                     <a href="/plugins" style={{ color: style.color3 }}>
//                       <Typography variant="h6">Shop All Plugins</Typography>
//                     </a>
//                   </Box>
//                 </Box>
//               ) : (
//                 <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
//                   <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
//                     <FormControl sx={{ minWidth: 180 }}>
//                       <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                       <Select
//                         value={sortOption}
//                         onChange={(e) => setSortOption(e.target.value)}
//                         size="small"
//                         sx={{ color: style.color4, backgroundColor: style.color1 }}
//                       >
//                         <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                         <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                       </Select>
//                     </FormControl>

//                     <TextField
//                       variant="outlined"
//                       placeholder="Search by name..."
//                       value={searchTerm}
//                       size="small"
//                       onChange={(e) => setSearchTerm(e.target.value)}
//                       sx={{ minWidth: 250, backgroundColor: style.color1, input: { color: style.color4 } }}
//                     />
//                   </Box>

//                   <Grid container spacing={3}>
//                     {filteredGear.map((product) => {
//                       const license = getLicenseBySKU(product.SKU);
//                       const machines = license?.machinesInstalledOn || [];

//                       return (
//                         <Grid item xs={12} sm={6} md={4} key={product.SKU}>
//                           <Card sx={{ backgroundColor: style.color1, color: style.color4 }}>
//                             <a href={`/products/${product.productURI}`}>
//                               <CardMedia
//                                 component="img"
//                                 image={product.images?.[0]?.URI || '/default-image.png'}
//                                 alt={product.images?.[0]?.altText || 'Product image'}
//                                 sx={{ maxHeight: 200, objectFit: 'contain', p: 1 }}
//                               />
//                             </a>
//                             <CardContent>
//                               <Typography variant="h6">
//                                 <a href={`/products/${product.productURI}`} style={{ color: style.color4 }}>{product.name}</a>
//                               </Typography>
//                               <Typography variant="body2">{product.shortDescription}</Typography>
//                               <Typography variant="body2" sx={{ mt: 1 }}>
//                                 Installed on {machines.length} device{machines.length !== 1 ? 's' : ''}
//                               </Typography>
//                               {machines.length > 0 && (
//                                 <Button variant="outlined" onClick={() => handleOpenDevicesModal(license)} sx={{ mt: 1 }}>
//                                   View Devices
//                                 </Button>
//                               )}
//                               {product.windowsDownloadURI && (
//                                 <Button href={product.windowsDownloadURI} target="_blank" variant="contained" sx={{ mt: 1 }}>
//                                   Download for Windows
//                                 </Button>
//                               )}
//                               {product.macDownloadURI && (
//                                 <Button href={product.macDownloadURI} target="_blank" variant="contained" sx={{ mt: 1 }}>
//                                   Download for Mac
//                                 </Button>
//                               )}
//                             </CardContent>
//                           </Card>
//                         </Grid>
//                       );
//                     })}
//                   </Grid>
//                 </Box>
//               )}
//             </>
//           )}

//           {selectedLicense && (
//             <Box
//               sx={{
//                 position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
//                 bgcolor: 'rgba(0,0,0,0.8)', display: isModalOpen ? 'flex' : 'none',
//                 alignItems: 'center', justifyContent: 'center', zIndex: 1300
//               }}
//               onClick={handleCloseDevicesModal}
//             >
//               <Box
//                 onClick={(e) => e.stopPropagation()}
//                 sx={{ backgroundColor: style.color1, p: 3, borderRadius: 2, width: 500, color: style.color4 }}
//               >
//                 <Typography variant="h6" mb={2}>Devices for SKU {selectedLicense.sku}</Typography>
//                 {selectedLicense.machinesInstalledOn.map((device) => (
//                   <Box key={device._id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//                     <Typography>{device.deviceName} ({device.osName})</Typography>
//                     <Button onClick={() => handleRemoveDevice(device._id)} size="small" variant="outlined">
//                       Remove
//                     </Button>
//                   </Box>
//                 ))}
//                 <Button onClick={handleCloseDevicesModal} sx={{ mt: 2 }} variant="contained">Close</Button>
//               </Box>
//             </Box>
//           )}
//         </Box>
//       </div>
//     </div>
//   );
// }



// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   CardMedia,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField
// } from '@mui/material';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';

// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');

//   useEffect(() => {
//     document.title = 'Parish Audio | My Gear';
//     getMyGear();
//   }, []);

//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');
//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });
//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products);
//         setLicenses(data.licenses)
//         console.log(data);
//       }
//     } catch (error) {
//       console.log(error);
//     }
//     setIsLoading(false);
//   };

//   const filteredGear = gear
//     .filter((item) => {
//       if (filter === 'Mac' && !item.macDownloadURI) return false;
//       if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//       if (!item.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
//       return true;
//     })
//     .sort((a, b) => {
//       if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//       if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//       return 0;
//     });

//   return (
//     <div className='image-background-1'>
//           <div className='dark-alpha-80'>
//     <Box sx={{ minHeight: '100vh', color: style.color4, py: 0 }}>
      

//       {isLoading ? (
//         <Box sx={{ textAlign: 'center', mt: 10 }}>
//           <LoadingSpinner />
//         </Box>
//       ) : (
//         <>
//           {gear.length === 0 ? (
//             <Box textAlign="center" mt={10}>
//               <Typography variant="h5">You do not yet have any equipment.</Typography>
//               <Box mt={3}>
//                 <a href="/plugins" style={{ color: style.color3 }}>
//                   <Typography variant="h6">Shop All Plugins</Typography>
//                 </a>
//               </Box>
//             </Box>
//           ) : (
//             <div>
//                 <div style={{maxWidth: 1200, margin:'auto', marginTop:'10px', marginBottom:'10px'}}>
//                     <h1 style={{marginTop:'0px'}}>My Gear</h1>
//                 </div>
//             <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2, backgroundColor:'black', borderColor:style.color1, borderWidth:'2px', borderStyle:'solid', py:2 }}>
//               {/* Centered Filters and Search */}
//               <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>

//               <FormControl sx={{ minWidth: 180 }}>
//                 <InputLabel
//                     sx={{
//                     color: style.color4,
//                     '&.Mui-focused': {
//                         color: style.color3,
//                     },
//                     }}
//                 >
//                     Sort
//                 </InputLabel>
//                 <Select
//                     value={sortOption}
//                     onChange={(e) => setSortOption(e.target.value)}
//                     size="small"
//                     sx={{
//                     color: style.color4,
//                     backgroundColor: style.color1,
//                     borderRadius: 1,
//                     '.MuiSvgIcon-root': { color: style.color4 },
//                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//                         borderColor: style.color3,
//                     },
//                     '& .MuiOutlinedInput-notchedOutline': {
//                         borderColor: 'transparent',
//                     },
//                     '&:hover .MuiOutlinedInput-notchedOutline': {
//                         borderColor: style.color3,
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color3,
//                     },
//                     }}
//                 >
//                     <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                     <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                 </Select>
//                 </FormControl>

//                 <TextField
//                 variant="outlined"
//                 placeholder="Search by name..."
//                 value={searchTerm}
//                 size="small"
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 sx={{
//                     minWidth: 250,
//                     backgroundColor: style.color1,
//                     input: { color: style.color4 },
//                     '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                         borderColor: 'transparent',
//                     },
//                     '&:hover fieldset': {
//                         borderColor: style.color3,
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color3,
//                     },
//                     },
//                     '& .MuiInputLabel-root': {
//                     color: style.color4,
//                     '&.Mui-focused': {
//                         color: style.color3,
//                     },
//                     },
//                 }}
//                 />
//               </Box>

//               {/* Gear Grid */}
//               <Box display="flex" justifyContent="center">
//                 <Grid container spacing={3} justifyContent="center" maxWidth="lg">
//                   {filteredGear.map((product) => (
//                     <Grid item xs={12} sm={6} md={4} key={product.SKU}>
//                       <Card
//                         sx={{
//                           height: '100%',
//                           display: 'flex',
//                           flexDirection: 'column',
//                           backgroundColor: style.color1,
//                           color: style.color4,
//                         }}
//                       >
//                         <a href={'/products/' + product.productURI}>
//                             <CardMedia
//                             component="img"
//                             image={product.images?.[0]?.URI || '/default-image.png'}
//                             alt={product.images?.[0]?.altText || 'Product image'}
//                             sx={{
//                                 maxHeight: 200,
//                                 objectFit: 'contain',
//                                 backgroundColor: 'rgba(0, 0, 0, 0.6)',
//                                 border: `5px solid ${style.color1}`,
//                                 borderRadius: 1,
//                                 p: 1,
//                                 boxSizing: 'border-box',
//                             }}
//                             /></a>
//                         <CardContent sx={{ flexGrow: 1 }}>
//                           <Typography variant="h6" gutterBottom>
//                           <a href={'/products/' + product.productURI}>{product.name}</a>
//                           </Typography>
//                           <Typography variant="body2" sx={{ mb: 2 }}>
//                             {product.shortDescription}
//                           </Typography>
//                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//                             {product.windowsDownloadURI && (
//                               <Button
//                                 variant="contained"
//                                 href={product.windowsDownloadURI}
//                                 target="_blank"
//                                 sx={{
//                                   backgroundColor: style.color2,
//                                   color: style.color4,
//                                   '&:hover': {
//                                     backgroundColor: style.color3,
//                                   },
//                                 }}
//                               >
//                                     Download for Windows

//                               </Button>
//                             )}
//                             {product.macDownloadURI && (
//                               <Button
//                                 variant="contained"
//                                 href={product.macDownloadURI}
//                                 target="_blank"
//                                 sx={{
//                                   backgroundColor: style.color2,
//                                   color: style.color4,
//                                   '&:hover': {
//                                     backgroundColor: style.color3,
//                                   },
//                                 }}
//                               >
//                                 Download for Mac
//                               </Button>
//                             )}
//                           </Box>
//                         </CardContent>
//                       </Card>
//                     </Grid>
//                   ))}
//                 </Grid>
//               </Box>
//             </Box>
//             </div>
//           )}
//         </>
//       )}
//     </Box>
//     </div>
//     </div>
//   );
// }